import { useMemo, useState } from 'react';

import {
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@cast/design-system';
import { FailedToLoad } from '@cast/shared-messages-ui';

import { WidgetLayout } from '../../../_components/ui/WidgetLayout';
import { ChartWithTopWorkloadsCard } from '../../../_components/ui/ChartWithTopWorkloadsCard';
import { WORKLOAD_CATEGORIES } from '../../../_constants';
import { useInventoryWorkloadsDashboardNavigation } from '../_hooks/useInventoryWorkloadsDashboardNavigation';
import { ReliabilityScatterChart } from './_components/ReliabilityScatterChart';
import {
  TopOffenderMetric,
  useReliabilityDashboardData,
} from './_hooks/useReliabilityDashboardData';

const TOGGLE_OPTIONS: { value: TopOffenderMetric; label: string }[] = [
  { value: 'errorRatePercent', label: 'Error rate' },
  { value: 'errorRate', label: 'Errors/s' },
  { value: 'latencyP95Ms', label: 'Latency' },
];

export const ReliabilityMapCard = () => {
  const { getWorkloadDashboardPath } =
    useInventoryWorkloadsDashboardNavigation();
  const [metric, setMetric] = useState<TopOffenderMetric>('errorRatePercent');
  const {
    scatterData,
    getTopOffenders,
    isLoading,
    isFetching = isLoading,
    error,
    showNotInstalled,
    refetch,
  } = useReliabilityDashboardData() as ReturnType<
    typeof useReliabilityDashboardData
  > & { isFetching?: boolean };

  const hasError = !!error;
  const isCardLoading = isLoading || (hasError && isFetching);

  const filteredScatterData = useMemo(() => {
    if (metric === 'errorRatePercent')
      return scatterData.filter((p) => p.errorRatePercent > 0);
    if (metric === 'errorRate')
      return scatterData.filter((p) => p.errorRate > 0);
    if (metric === 'latencyP95Ms')
      return scatterData.filter((p) => p.latencyP95Ms > 0);
    return scatterData;
  }, [scatterData, metric]);

  if (showNotInstalled) {
    return (
      <WidgetLayout testId="reliability-map-card" padding={false}>
        <Stack
          alignItems="center"
          justifyContent="center"
          height="100%"
          gap={8}
          px={24}
        >
          <Typography variant="h5" textAlign="center">
            Reliability metrics not installed
          </Typography>
          <Typography
            variant="P14R"
            color="textSecondary"
            textAlign="center"
            maxWidth={480}
          >
            Install the reliability metrics collector to view the reliability
            map and workload health metrics.
          </Typography>
        </Stack>
      </WidgetLayout>
    );
  }

  const topOffenders = getTopOffenders(metric);

  const leftHeader = (
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="space-between"
      flex={1}
      gap={12}
    >
      <Typography variant="A1">Reliability map</Typography>
      <ToggleButtonGroup
        value={metric}
        onChange={(_, value) => {
          if (value) {
            setMetric(value as TopOffenderMetric);
          }
        }}
        size="sm"
        testId="reliability-map-metric-toggle"
      >
        {TOGGLE_OPTIONS.map((option) => (
          <ToggleButton
            key={option.value}
            value={option.value}
            text={option.label}
            testId={`reliability-map-metric-${option.value}`}
          />
        ))}
      </ToggleButtonGroup>
    </Stack>
  );

  const rightHeader = <Typography variant="A1">Highest to Lowest</Typography>;

  const chartWithTopWorkloadsCard = (
    <ChartWithTopWorkloadsCard
      testId="reliability-map-card"
      leftHeader={leftHeader}
      rightHeader={rightHeader}
      chart={
        <ReliabilityScatterChart
          data={filteredScatterData}
          metric={metric}
          isLoading={isCardLoading}
        />
      }
      items={topOffenders}
      isListLoading={isCardLoading && !topOffenders.length}
      getItemHref={(item) =>
        getWorkloadDashboardPath(item.id, WORKLOAD_CATEGORIES.RELIABILITY)
      }
    />
  );

  return isCardLoading ? (
    chartWithTopWorkloadsCard
  ) : hasError ? (
    <WidgetLayout testId="reliability-map-card" padding={false}>
      <FailedToLoad title="Failed to load reliability map" refresh={refetch} />
    </WidgetLayout>
  ) : (
    chartWithTopWorkloadsCard
  );
};
